/**
 * DocumentDetail — /documents/$id (v0.12.3)
 *
 * Owner: drafts/55 v0.12 — document detail surface.
 * Purpose: render full document metadata + per-site effectivity badges
 *          + quality-event back-links + signature timeline + action bar.
 *
 * The hooks driving this screen all existed before this commit:
 *   - useDocumentDetailQuery       — new (this drop)
 *   - useDocSiteEffectivity        — V26 hook (multi-site badges)
 *   - useDocumentQualityEvents     — drafts/53 §B (CAPA / Dev / OOS back-links)
 *
 * Editor handoff: clicking "Open in editor" routes to /documents/$id/edit
 * which is the existing DocumentEditor (OnlyOffice handshake from v0.8).
 */

import { useMemo, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useDocSiteEffectivity, useDocumentQualityEvents, type DocSiteEffectivity } from '../../api/orgHooks';
import { useCurrentUser } from '../../api/hooks';
import {
  useDocumentDetailQuery,
  useDocVisibleSites,
  isDocVisible,
  useEffectivityPlan,
  useScheduleEffective,
  useDocumentVersions,
  useReviseDocument,
  useDocumentLinks,
  useAddDocumentLink,
  useRemoveDocumentLink,
} from './useDocumentDetailQuery';
import { useDocumentLockQuery, useCheckoutMutation, useCheckinMutation } from './useDocumentLock';
import { useDocumentChangeControls } from './useDocumentChangeControls';
import { useDocumentTraining } from './useDocumentTraining';
import { apiFetch } from '../../api/client';
import styles from './DocumentDetail.module.css';

// B-7 — the controlled-document lifecycle, always visible with the current stage
// highlighted (Mohanad review / drafts/63). Statuses map to a stage index; the
// stages before are "done", the matched one "current", the rest "upcoming".
// B-7 / B-7b — lifecycle stages, each with a per-stage SLA target (days) for the
// active authoring/review/approval phases. Terminal stages have no SLA. The
// tracker flags the current stage if it has been sitting past its SLA target.
const LIFECYCLE: { key: string; label: string; slaDays?: number }[] = [
  { key: 'DRAFT', label: 'Draft', slaDays: 30 },
  { key: 'IN_REVIEW', label: 'In Review', slaDays: 10 },
  { key: 'PENDING_APPROVAL', label: 'QA Approval', slaDays: 5 },
  { key: 'APPROVED_PENDING_EFFECTIVE', label: 'Pending Effective', slaDays: 30 }, // B-6a
  { key: 'EFFECTIVE', label: 'Effective' },
  { key: 'SUPERSEDED', label: 'Superseded' },
  { key: 'OBSOLETE', label: 'Obsolete' },
  { key: 'ARCHIVED', label: 'Archived' },
];
function lifecycleIndex(status: string): number {
  const i = LIFECYCLE.findIndex((s) => s.key === status);
  if (i >= 0) return i;
  // 'APPROVED' is the legacy "approved-but-not-yet-effective" status — it maps
  // to the same step as APPROVED_PENDING_EFFECTIVE (B-6a).
  if (status === 'APPROVED') return LIFECYCLE.findIndex((s) => s.key === 'APPROVED_PENDING_EFFECTIVE');
  return 0;                                    // unknown/early → Draft
}

function statusBand(status: string): 'green' | 'amber' | 'red' | 'grey' {
  switch (status) {
    case 'EFFECTIVE':
      return 'green';
    case 'IN_REVIEW':
    case 'PENDING_APPROVAL':
    case 'APPROVED_PENDING_EFFECTIVE': // B-6a — approved, awaiting effective date
      return 'amber';
    case 'WITHDRAWN':
    case 'SUPERSEDED':
      return 'grey';
    case 'DRAFT':
    default:
      return 'amber';
  }
}

function effectivityStatusColor(status: DocSiteEffectivity['status']): 'green' | 'amber' | 'red' | 'grey' {
  switch (status) {
    case 'EFFECTIVE':
      return 'green';
    case 'PENDING':
      return 'amber';
    case 'NOT_APPLICABLE':
    case 'WITHDRAWN':
    case 'SUPERSEDED':
      return 'grey';
    default:
      return 'amber';
  }
}

function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' });
  } catch {
    return iso;
  }
}

function fmtDateTime(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function DocumentDetail() {
  const { id } = useParams({ strict: false }) as { id: string };
  const docQ = useDocumentDetailQuery(id);
  const visQ = useDocVisibleSites();
  const effectivityQ = useDocSiteEffectivity(id);
  const eventsQ = useDocumentQualityEvents(id);
  const changeControlsQ = useDocumentChangeControls(id);
  const trainingQ = useDocumentTraining(id);
  const userQ = useCurrentUser();
  const lockQ = useDocumentLockQuery(id);
  const checkout = useCheckoutMutation(id);
  const checkin = useCheckinMutation(id);
  const planQ = useEffectivityPlan(id);
  const scheduleEffective = useScheduleEffective(id);
  const versionsQ = useDocumentVersions(id);
  const revise = useReviseDocument(id);
  const linksQ = useDocumentLinks(id);
  const addLink = useAddDocumentLink(id);
  const removeLink = useRemoveDocumentLink(id);
  const [lockMsg, setLockMsg] = useState<string | null>(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [leadDays, setLeadDays] = useState(7);
  const [scheduleMsg, setScheduleMsg] = useState<string | null>(null);
  const [reviseMsg, setReviseMsg] = useState<string | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkDocNumber, setLinkDocNumber] = useState('');
  const [linkKind, setLinkKind] = useState('FORM');
  const [linkMsg, setLinkMsg] = useState<string | null>(null);

  const lock = lockQ.data?.lock ?? null;
  const lockedByMe = !!lock && lock.locked_by_user_id === (userQ.data?.id ?? '');

  // B-6a — schedule the effective date (approve → effective +N days). On success
  // the detail + plan invalidate, so the lifecycle stepper + banner repaint.
  async function doSchedule() {
    setScheduleMsg(null);
    try {
      await scheduleEffective.mutateAsync({ leadDays });
      setScheduleOpen(false);
    } catch (e: unknown) {
      setScheduleMsg(e instanceof Error ? e.message : 'Could not schedule the effective date.');
    }
  }
  // B-6b — start a revision (creates the next DRAFT version; the current effective
  // version stays controlled until the new one becomes effective, then supersedes).
  async function doRevise() {
    setReviseMsg(null);
    try {
      await revise.mutateAsync();
    } catch (e: unknown) {
      setReviseMsg(e instanceof Error ? e.message : 'Could not start a revision.');
    }
  }
  // B-6c — link a child document (a form / WI / reference) to this one.
  async function doAddLink() {
    setLinkMsg(null);
    const num = linkDocNumber.trim();
    if (!num) { setLinkMsg('Enter the document number to link.'); return; }
    try {
      await addLink.mutateAsync({ childDocNumber: num, linkKind });
      setLinkDocNumber('');
      setLinkOpen(false);
    } catch (e: unknown) {
      setLinkMsg(e instanceof Error ? e.message : 'Could not link the document.');
    }
  }
  async function doRemoveLink(linkId: string) {
    setLinkMsg(null);
    try {
      await removeLink.mutateAsync(linkId);
    } catch (e: unknown) {
      setLinkMsg(e instanceof Error ? e.message : 'Could not remove the link.');
    }
  }

  async function doCheckout() {
    setLockMsg(null);
    try { await checkout.mutateAsync({}); }
    catch (e: unknown) { setLockMsg(e instanceof Error ? e.message : 'Check-out failed.'); }
  }
  async function doCheckin(force = false) {
    setLockMsg(null);
    try { await checkin.mutateAsync({ force }); }
    catch (e: unknown) { setLockMsg(e instanceof Error ? e.message : 'Check-in failed.'); }
  }
  // DMS-14: record the controlled-print event (audit trail → print.requests),
  // best-effort, then print. The screen-hidden watermark + header/footer (above)
  // paint on window.print() so the hard copy is stamped "UNCONTROLLED WHEN PRINTED".
  async function doPrint() {
    try { await apiFetch(`/api/v1/documents/${encodeURIComponent(id)}/print-request`, { method: 'POST', body: {} }); }
    catch { /* never block printing on a transient audit-record failure */ }
    window.print();
  }

  const effectivityRows = useMemo(() => effectivityQ.data?.items ?? [], [effectivityQ.data]);
  const eventRows = useMemo(() => eventsQ.data?.items ?? [], [eventsQ.data]);
  const changeControlRows = useMemo(() => changeControlsQ.data?.items ?? [], [changeControlsQ.data]);
  const training = trainingQ.data ?? null;

  if (docQ.isLoading) {
    return (
      <main className={styles.page}>
        <div className={styles.empty}>Loading document…</div>
      </main>
    );
  }
  if (docQ.isError || !docQ.data) {
    return (
      <main className={styles.page}>
        <div className={styles.empty}>
          Document not found. <Link to="/workbench">Back to workbench</Link>
        </div>
      </main>
    );
  }
  const d = docQ.data;
  // P4 / DMS-30 — site-visibility guard for the direct-URL detail view. A scoped
  // user who reaches an out-of-scope document by UUID gets a denial, not the doc.
  if (!isDocVisible(visQ.data, d.site_id)) {
    return (
      <main className={styles.page}>
        <div className={styles.empty}>
          You don't have access to this document at its site. <Link to="/documents/dashboard">Back to dashboard</Link>
        </div>
      </main>
    );
  }
  const band = statusBand(d.status);

  // B-6a — effective-date control. The doc is "pending effective" when QA has
  // approved it but the planned effective date hasn't arrived. "Schedule
  // effective" is offered for non-terminal docs that aren't already pending /
  // effective (the action records the planned go-live date + audit trail).
  const plan = planQ.data ?? null;
  const isPendingEffective = d.status === 'APPROVED_PENDING_EFFECTIVE' || d.status === 'APPROVED';
  const TERMINAL = ['EFFECTIVE', 'SUPERSEDED', 'OBSOLETE', 'ARCHIVED', 'WITHDRAWN'];
  const canSchedule = !TERMINAL.includes(d.status) && !isPendingEffective;
  const plannedDate = plan?.effective_date ?? null;
  const daysRemaining = plan?.days_remaining ?? null;

  // B-6b — version history + supersession. "Revise" is offered once a document
  // is EFFECTIVE (creates the next DRAFT version; the prior version is superseded
  // automatically when the new one goes effective).
  const versions = versionsQ.data?.items ?? [];
  const canRevise = d.status === 'EFFECTIVE';

  // B-6c — linked documents (forms / WIs / references). When the document is
  // revised, the linked documents may need updating too — so the Revise/Schedule
  // actions show a reminder when links exist.
  const childLinks = linksQ.data?.children ?? [];
  const parentLinks = linksQ.data?.parents ?? [];
  const linkCount = childLinks.length + parentLinks.length;
  const LINK_KIND_LABEL: Record<string, string> = {
    FORM: 'Form', WORK_INSTRUCTION: 'Work instruction', REFERENCE: 'Reference',
    ATTACHMENT: 'Attachment', TEMPLATE: 'Template', SUPERSEDES: 'Supersedes',
  };

  return (
    <main className={styles.page}>
      {/* eDMS E4 — controlled-print watermark + header/footer. Screen-hidden;
          only paints when the page is printed (window.print / Ctrl-P). */}
      <div className={styles.printWatermark} aria-hidden="true">UNCONTROLLED WHEN PRINTED</div>
      <div className={styles.printHeader} aria-hidden="true">
        <span>{d.doc_number} · {d.document_type} · {d.status}</span>
        <span>QWise controlled document</span>
      </div>
      <div className={styles.printFooter} aria-hidden="true">
        Printed by {userQ.data?.full_name ?? 'QWise user'} · {new Date().toLocaleString()} · This is an UNCONTROLLED copy — verify against the current controlled version in QWise before use.
      </div>

      <header className={styles.header}>
        <nav className={styles.crumbs} aria-label="Breadcrumb">
          <Link to="/workbench" className={styles.crumb}>
            Documents
          </Link>
          <span className={styles.crumbSep} aria-hidden>
            /
          </span>
          <span className={styles.crumbCurrent}>{d.doc_number}</span>
        </nav>
        <div className={styles.titleRow}>
          <div className={styles.titleBlock}>
            <h1 className={styles.title}>
              <span className={styles.docNumber}>{d.doc_number}</span>
              <span className={styles.dot} aria-hidden>
                ·
              </span>
              <span className={styles.titleText}>{d.title}</span>
            </h1>
            <div className={styles.subline}>
              <span className={`${styles.statusPill} ${styles[`status_${band}`]}`}>{d.status}</span>
              <span className={styles.subText}>{d.document_type}</span>
              <span className={styles.subText}>{d.classification}</span>
              {d.legal_hold_active && (
                <span className={`${styles.statusPill} ${styles.status_red}`}>Legal Hold</span>
              )}
              {lock && (
                <span className={`${styles.lockPill} ${lockedByMe ? styles.lockMine : styles.lockOther}`}>
                  🔒 {lockedByMe ? 'Checked out by you' : `Locked by ${lock.locked_by_name}`}
                </span>
              )}
            </div>
          </div>
          <div className={styles.actions}>
            <Link
              to="/documents/$id/read"
              params={{ id: d.document_id }}
              className={`${styles.btn} ${styles.btn_primary}`}
            >
              Read document
            </Link>
            <button
              type="button"
              className={`${styles.btn} ${styles.btn_neutral}`}
              onClick={() => void doPrint()}
              title="Records a controlled-print audit entry, then prints a watermarked copy"
            >
              Print controlled copy
            </button>
            {lock ? (
              lockedByMe ? (
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btn_primary}`}
                  onClick={() => void doCheckin()}
                  disabled={checkin.isPending}
                >
                  {checkin.isPending ? 'Checking in…' : 'Check in'}
                </button>
              ) : (
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btn_neutral}`}
                  onClick={() => void doCheckin(true)}
                  disabled={checkin.isPending}
                  title="Admin override — releases another user's lock"
                >
                  Override check-in
                </button>
              )
            ) : (
              <button
                type="button"
                className={`${styles.btn} ${styles.btn_primary}`}
                onClick={() => void doCheckout()}
                disabled={checkout.isPending}
              >
                {checkout.isPending ? 'Checking out…' : 'Check out to edit'}
              </button>
            )}
            <Link
              to="/documents/$id/edit"
              params={{ id: d.document_id }}
              className={`${styles.btn} ${styles.btn_neutral} ${lock && !lockedByMe ? styles.btnDisabled : ''}`}
              aria-disabled={lock && !lockedByMe ? true : undefined}
              onClick={(e) => { if (lock && !lockedByMe) e.preventDefault(); }}
            >
              Open in editor
            </Link>
            <Link
              to="/documents/$id/redline"
              params={{ id: d.document_id }}
              className={`${styles.btn} ${styles.btn_neutral}`}
            >
              Review redlines
            </Link>
            {/* B-6a — schedule the effective date (approved → effective +N days). */}
            {canSchedule && (
              <button
                type="button"
                className={`${styles.btn} ${styles.btn_neutral}`}
                onClick={() => { setScheduleOpen((v) => !v); setScheduleMsg(null); }}
                title="Approve and set a future effective date so training can complete first"
              >
                Schedule effective date
              </button>
            )}
            {/* B-6b — revise (start the next version). */}
            {canRevise && (
              <button
                type="button"
                className={`${styles.btn} ${styles.btn_neutral}`}
                onClick={() => void doRevise()}
                disabled={revise.isPending}
                title="Create the next version as a draft; the current version stays effective until the new one is approved"
              >
                {revise.isPending ? 'Starting revision…' : 'Revise (new version)'}
              </button>
            )}
            {/* Removed a second, non-audited "Print controlled copy" (bare
                window.print) — the audited print action elsewhere records a
                print-request before printing. (drafts/61 F1-7.) */}
          </div>
        </div>
        {/* B-6a — inline scheduler: pick the lead time, confirm. */}
        {canSchedule && scheduleOpen && (
          <div className={styles.scheduleBox} role="group" aria-label="Schedule effective date">
            <label className={styles.scheduleLabel} htmlFor="lead-days">
              Effective in
            </label>
            <input
              id="lead-days"
              type="number"
              min={0}
              max={365}
              value={leadDays}
              onChange={(e) => setLeadDays(Math.max(0, Math.min(365, Number(e.target.value) || 0)))}
              className={styles.scheduleInput}
            />
            <span className={styles.scheduleUnit}>day(s) — training must complete before then</span>
            <button
              type="button"
              className={`${styles.btn} ${styles.btn_primary}`}
              onClick={() => void doSchedule()}
              disabled={scheduleEffective.isPending}
            >
              {scheduleEffective.isPending ? 'Scheduling…' : 'Confirm effective date'}
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btn_neutral}`}
              onClick={() => { setScheduleOpen(false); setScheduleMsg(null); }}
            >
              Cancel
            </button>
          </div>
        )}
        {scheduleMsg && (
          <div className={styles.lockBanner} role="alert">{scheduleMsg}</div>
        )}
        {reviseMsg && (
          <div className={styles.lockBanner} role="alert">{reviseMsg}</div>
        )}
        {/* B-6c — revision reminder: linked documents may need updating too. */}
        {linkCount > 0 && (canRevise || canSchedule || isPendingEffective) && (
          <div className={styles.lockBannerInfo} role="status">
            🔗 This document has {linkCount} linked document{linkCount === 1 ? '' : 's'} (forms / work instructions / references). When you revise it, review the linked documents too — they may need updating.
          </div>
        )}
        {lockMsg && (
          <div className={styles.lockBanner} role="alert">{lockMsg}</div>
        )}
        {lock && !lockedByMe && (
          <div className={styles.lockBannerInfo} role="status">
            🔒 {lock.locked_by_name} checked this document out{lock.locked_at_utc ? ` on ${new Date(lock.locked_at_utc).toLocaleString()}` : ''}. Editing is blocked until they check it in.
          </div>
        )}
      </header>

      {/* B-7 — always-visible controlled-document lifecycle tracker. */}
      <nav className={styles.lifecycle} aria-label="Document lifecycle">
        {d.status === 'WITHDRAWN' ? (
          <span className={styles.lifecycleWithdrawn}>Withdrawn — terminated before becoming effective</span>
        ) : (
          LIFECYCLE.map((s, i) => {
            const cur = lifecycleIndex(d.status);
            const state = i < cur ? 'done' : i === cur ? 'current' : 'future';
            // B-7b — per-stage SLA: days in the CURRENT stage vs its target.
            let sla: { days: number; target: number; breach: boolean } | null = null;
            if (state === 'current' && s.slaDays && d.updated_at_utc) {
              const days = Math.max(0, Math.floor((Date.now() - new Date(d.updated_at_utc).getTime()) / 86400000));
              sla = { days, target: s.slaDays, breach: days > s.slaDays };
            }
            return (
              <span
                key={s.key}
                className={`${styles.lcStep} ${state === 'done' ? styles.lcDone : state === 'current' ? styles.lcCurrent : styles.lcFuture}`}
                aria-current={state === 'current' ? 'step' : undefined}
              >
                <span className={styles.lcDot} aria-hidden>{state === 'done' ? '✓' : i + 1}</span>
                <span className={styles.lcLabel}>{s.label}</span>
                {sla && (
                  <span
                    style={{
                      display: 'block', fontSize: 11, fontWeight: 600, marginTop: 2,
                      color: sla.breach ? 'var(--color-danger-text, #b42318)' : 'var(--color-text-muted, #6B7989)',
                    }}
                    title={sla.breach ? `SLA breached — ${sla.days}d in stage (target ${sla.target}d)` : `${sla.days}d in stage (SLA ${sla.target}d)`}
                  >
                    {sla.breach ? `⚠ ${sla.days}d · SLA ${sla.target}d` : `${sla.days}d · SLA ${sla.target}d`}
                  </span>
                )}
              </span>
            );
          })
        )}
      </nav>

      {/* B-6a — "becomes effective on …" banner for an approved-pending document. */}
      {isPendingEffective && plannedDate && (
        <div className={styles.effectiveBanner} role="status">
          <span className={styles.effectiveIcon} aria-hidden>📅</span>
          <span>
            Approved — becomes <strong>Effective on {fmtDate(plannedDate)}</strong>
            {typeof daysRemaining === 'number' && daysRemaining > 0
              ? ` (in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'})`
              : typeof daysRemaining === 'number' && daysRemaining <= 0
                ? ' (due — activates on next access)'
                : ''}
            . Training on this version must complete before the effective date.
          </span>
        </div>
      )}

      <section className={styles.panel} aria-labelledby="doc-meta">
        <header className={styles.panelHead}>
          <h2 id="doc-meta" className={styles.panelTitle}>
            Metadata
          </h2>
        </header>
        <dl className={styles.metaGrid}>
          <div>
            <dt>Owner</dt>
            <dd>{d.owner_name ?? d.owner_user_id}</dd>
          </div>
          <div>
            <dt>Department</dt>
            <dd>{d.department_name ?? d.department ?? '—'}</dd>
          </div>
          <div>
            <dt>Primary site</dt>
            <dd className={styles.mono}>{d.site_code ?? '—'}</dd>
          </div>
          <div>
            <dt>Retention policy</dt>
            <dd>{d.retention_policy_name ?? 'Not assigned'}</dd>
          </div>
          <div>
            <dt>Retention ends</dt>
            <dd>{fmtDate(d.retention_end_at)}</dd>
          </div>
          <div>
            <dt>Created</dt>
            <dd>{fmtDateTime(d.created_at_utc)}</dd>
          </div>
          <div>
            <dt>Last updated</dt>
            <dd>{fmtDateTime(d.updated_at_utc)}</dd>
          </div>
          <div>
            <dt>Source artifact</dt>
            <dd className={styles.mono}>{d.source_artifact_ref ?? '—'}</dd>
          </div>
        </dl>
      </section>

      {/* B-6b — version history. One version is EFFECTIVE at a time; superseded
          versions are read-only history. */}
      <section className={styles.panel} aria-labelledby="doc-versions">
        <header className={styles.panelHead}>
          <h2 id="doc-versions" className={styles.panelTitle}>Version history</h2>
          <span className={styles.panelCount}>{versions.length}</span>
        </header>
        {versions.length === 0 ? (
          <div className={styles.empty}>
            No version records yet. A version is created when the document is uploaded, scheduled effective, or revised.
          </div>
        ) : (
          <ol className={styles.versionList}>
            {versions.map((v) => {
              const s = (v.state || '').toUpperCase();
              const isEffective = s === 'EFFECTIVE';
              const isSuperseded = s === 'SUPERSEDED' || s === 'OBSOLETE' || s === 'ARCHIVED';
              const band = isSuperseded ? 'grey' : statusBand(s);
              return (
                <li
                  key={v.document_version_id}
                  className={`${styles.versionRow} ${isEffective ? styles.versionEffective : ''} ${isSuperseded ? styles.versionSuperseded : ''}`}
                >
                  <span className={styles.versionTag}>v{v.version}</span>
                  <span className={`${styles.statusPill} ${styles[`status_${band}`]}`}>{s}</span>
                  <span className={styles.versionMeta}>
                    {isEffective && v.effective_date ? `Effective ${fmtDate(v.effective_date)}` : null}
                    {isSuperseded ? 'Read-only — superseded by a newer version' : null}
                    {!isEffective && !isSuperseded ? `Created ${fmtDate(v.created_at_utc)}` : null}
                  </span>
                  <span className={styles.versionAuthor}>{v.author_name}</span>
                </li>
              );
            })}
          </ol>
        )}
      </section>

      {/* B-6c — linked documents. An SOP and its forms / WIs are linked so
          they're reviewed (and revised) together. */}
      <section className={styles.panel} aria-labelledby="doc-links">
        <header className={styles.panelHead}>
          <h2 id="doc-links" className={styles.panelTitle}>Linked documents</h2>
          <span className={styles.panelCount}>{linkCount}</span>
          <button
            type="button"
            className={`${styles.btn} ${styles.btn_neutral} ${styles.panelHeadAction}`}
            onClick={() => { setLinkOpen((v) => !v); setLinkMsg(null); }}
          >
            {linkOpen ? 'Close' : '+ Link a document'}
          </button>
        </header>
        {linkOpen && (
          <div className={styles.scheduleBox} role="group" aria-label="Link a document">
            <label className={styles.scheduleLabel} htmlFor="link-doc-number">Document #</label>
            <input
              id="link-doc-number"
              type="text"
              value={linkDocNumber}
              onChange={(e) => setLinkDocNumber(e.target.value)}
              placeholder="e.g. FRM-AUTO-101"
              className={styles.linkInput}
            />
            <select
              aria-label="Link kind"
              value={linkKind}
              onChange={(e) => setLinkKind(e.target.value)}
              className={styles.linkSelect}
            >
              <option value="FORM">Form</option>
              <option value="WORK_INSTRUCTION">Work instruction</option>
              <option value="REFERENCE">Reference</option>
              <option value="ATTACHMENT">Attachment</option>
              <option value="TEMPLATE">Template</option>
            </select>
            <button
              type="button"
              className={`${styles.btn} ${styles.btn_primary}`}
              onClick={() => void doAddLink()}
              disabled={addLink.isPending}
            >
              {addLink.isPending ? 'Linking…' : 'Link'}
            </button>
          </div>
        )}
        {linkMsg && <div className={styles.lockBanner} role="alert">{linkMsg}</div>}
        {linkCount === 0 && !linkOpen && (
          <div className={styles.empty}>
            No linked documents. Link the forms, work instructions, or references this document governs.
          </div>
        )}
        {childLinks.length > 0 && (
          <>
            <h3 className={styles.linkGroupTitle}>Forms &amp; work instructions</h3>
            <ul className={styles.linkList}>
              {childLinks.map((l) => (
                <li key={l.link_id} className={styles.linkRow}>
                  <span className={styles.linkKindTag}>{LINK_KIND_LABEL[l.link_kind] ?? l.link_kind}</span>
                  <Link to="/documents/$id" params={{ id: l.document_id }} className={styles.linkDoc}>
                    <span className={styles.mono}>{l.doc_number}</span> · {l.title}
                  </Link>
                  <span className={`${styles.statusPill} ${styles[`status_${statusBand((l.status || '').toUpperCase())}`]}`}>{l.status}</span>
                  <button
                    type="button"
                    className={styles.linkRemove}
                    onClick={() => void doRemoveLink(l.link_id)}
                    disabled={removeLink.isPending}
                    aria-label={`Remove link to ${l.doc_number}`}
                    title="Remove link"
                  >×</button>
                </li>
              ))}
            </ul>
          </>
        )}
        {parentLinks.length > 0 && (
          <>
            <h3 className={styles.linkGroupTitle}>Referenced by</h3>
            <ul className={styles.linkList}>
              {parentLinks.map((l) => (
                <li key={l.link_id} className={styles.linkRow}>
                  <span className={styles.linkKindTag}>{LINK_KIND_LABEL[l.link_kind] ?? l.link_kind}</span>
                  <Link to="/documents/$id" params={{ id: l.document_id }} className={styles.linkDoc}>
                    <span className={styles.mono}>{l.doc_number}</span> · {l.title}
                  </Link>
                  <span className={`${styles.statusPill} ${styles[`status_${statusBand((l.status || '').toUpperCase())}`]}`}>{l.status}</span>
                  <button
                    type="button"
                    className={styles.linkRemove}
                    onClick={() => void doRemoveLink(l.link_id)}
                    disabled={removeLink.isPending}
                    aria-label={`Remove link to ${l.doc_number}`}
                    title="Remove link"
                  >×</button>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <section className={styles.panel} aria-labelledby="doc-effectivity">
        <header className={styles.panelHead}>
          <h2 id="doc-effectivity" className={styles.panelTitle}>
            Per-site effectivity
          </h2>
          <span className={styles.panelCount}>{effectivityRows.length}</span>
        </header>
        {effectivityQ.isLoading && <div className={styles.empty}>Loading site effectivity…</div>}
        {!effectivityQ.isLoading && effectivityRows.length === 0 && (
          <div className={styles.empty}>No per-site effectivity defined for this document yet.</div>
        )}
        {effectivityRows.length > 0 && (
          <div className={styles.effectGrid}>
            {effectivityRows.map((row) => {
              const band = effectivityStatusColor(row.status);
              return (
                <div key={row.effectivity_id} className={`${styles.effectCard} ${styles[`effect_${band}`]}`}>
                  <div className={styles.effectHead}>
                    <span className={styles.effectCode}>{row.site_code}</span>
                    <span className={`${styles.statusPill} ${styles[`status_${band}`]}`}>{row.status}</span>
                  </div>
                  <div className={styles.effectName}>{row.site_name}</div>
                  <dl className={styles.effectDl}>
                    <div>
                      <dt>Effective at</dt>
                      <dd>{fmtDate(row.effective_at_utc)}</dd>
                    </div>
                    {row.pending_reason && (
                      <div>
                        <dt>Pending reason</dt>
                        <dd>{row.pending_reason}</dd>
                      </div>
                    )}
                    {row.appendix_summary && (
                      <div>
                        <dt>Site appendix</dt>
                        <dd>{row.appendix_summary}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className={styles.panel} aria-labelledby="doc-events">
        <header className={styles.panelHead}>
          <h2 id="doc-events" className={styles.panelTitle}>
            Linked quality events
          </h2>
          <span className={styles.panelCount}>{eventRows.length}</span>
        </header>
        {eventsQ.isLoading && <div className={styles.empty}>Loading linked events…</div>}
        {!eventsQ.isLoading && eventRows.length === 0 && (
          <div className={styles.empty}>No CAPA / deviation / OOS events reference this document.</div>
        )}
        {eventRows.length > 0 && (
          <ul className={styles.eventList}>
            {eventRows.map((e) => {
              const kind = String(e.kind ?? 'OTHER').toUpperCase();
              const kindLower = kind.toLowerCase();
              return (
                <li key={`${kind}-${e.id}`} className={styles.eventRow}>
                  <Link to={e.link ?? '#'} className={styles.eventLink}>
                    <span className={`${styles.eventKind} ${styles[`kind_${kindLower}`]}`}>{kind}</span>
                    <span className={styles.eventNumber}>{e.number ?? '—'}</span>
                    <span className={styles.eventTitle}>{e.title ?? ''}</span>
                    <span className={styles.eventStatus}>{e.status ?? ''}</span>
                    {e.criticality && <span className={styles.eventBadge}>{e.criticality}</span>}
                    {e.classification && <span className={styles.eventBadge}>{e.classification}</span>}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className={styles.panel} aria-labelledby="doc-change-controls">
        <header className={styles.panelHead}>
          <h2 id="doc-change-controls" className={styles.panelTitle}>
            Change controls
          </h2>
          <span className={styles.panelCount}>{changeControlRows.length}</span>
        </header>
        {changeControlsQ.isLoading && <div className={styles.empty}>Loading change controls…</div>}
        {!changeControlsQ.isLoading && changeControlRows.length === 0 && (
          <div className={styles.empty}>No change requests reference this document.</div>
        )}
        {changeControlRows.length > 0 && (
          <ul className={styles.eventList}>
            {changeControlRows.map((c) => (
              <li key={c.change_request_id} className={styles.eventRow}>
                <div className={styles.eventLink}>
                  <span className={`${styles.eventKind} ${styles.kind_change}`}>CR</span>
                  <span className={styles.eventNumber}>{c.cr_number}</span>
                  <span className={styles.eventTitle}>{c.title}</span>
                  <span className={styles.eventStatus}>{c.status.replace(/_/g, ' ')}</span>
                  <span className={styles.eventBadge}>{c.change_type.replace(/_/g, ' ')}</span>
                  {c.effective_at_utc && (
                    <span className={styles.eventBadge}>eff. {fmtDate(c.effective_at_utc)}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={styles.panel} aria-labelledby="doc-training">
        <header className={styles.panelHead}>
          <h2 id="doc-training" className={styles.panelTitle}>
            Read training
          </h2>
          {training && !training.gated && (
            <span className={styles.panelCount}>
              {training.summary.completed}/{training.summary.total}
            </span>
          )}
        </header>
        {trainingQ.isLoading && <div className={styles.empty}>Loading training status…</div>}
        {training && training.gated && (
          <div className={styles.empty}>
            🔒 {training.gate_reason ?? 'Training is assigned once this document is Effective.'}
          </div>
        )}
        {training && !training.gated && (
          <>
            <div className={styles.trainStrip}>
              <div className={styles.trainStat}>
                <span className={styles.trainPct}>{training.summary.pct_complete}%</span>
                <span className={styles.trainLabel}>trained on current version</span>
              </div>
              <div className={styles.trainCounts}>
                <span className={`${styles.trainBadge} ${styles.trainOk}`}>{training.summary.completed} completed</span>
                {training.summary.overdue > 0 && (
                  <span className={`${styles.trainBadge} ${styles.trainOverdue}`}>{training.summary.overdue} overdue</span>
                )}
                {training.summary.assigned > 0 && (
                  <span className={`${styles.trainBadge} ${styles.trainPending}`}>{training.summary.assigned} assigned</span>
                )}
              </div>
            </div>
            <ul className={styles.trainRoster}>
              {training.roster.map((r, i) => (
                <li key={`${r.user_name}-${i}`} className={styles.trainRow}>
                  <span className={styles.trainName}>{r.user_name}</span>
                  <span className={styles.trainRole}>{r.role}</span>
                  <span
                    className={`${styles.trainState} ${
                      r.state === 'COMPLETED' ? styles.trainOk : r.state === 'OVERDUE' ? styles.trainOverdue : styles.trainPending
                    }`}
                  >
                    {r.state === 'COMPLETED' && r.completed_at
                      ? `Completed ${fmtDate(r.completed_at)}`
                      : r.state === 'OVERDUE'
                        ? `Overdue (due ${fmtDate(r.due_date)})`
                        : `Due ${fmtDate(r.due_date)}`}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <section className={styles.panel} aria-labelledby="doc-timeline">
        <header className={styles.panelHead}>
          <h2 id="doc-timeline" className={styles.panelTitle}>
            Signature & lifecycle timeline
          </h2>
        </header>
        <ol className={styles.timeline}>
          <li className={styles.timelineItem}>
            <span className={styles.timelineDot} aria-hidden />
            <div className={styles.timelineBody}>
              <span className={styles.timelineTime}>{fmtDateTime(d.created_at_utc)}</span>
              <span className={styles.timelineTitle}>Document created</span>
              <span className={styles.timelineSub}>by {d.owner_name ?? d.owner_user_id}</span>
            </div>
          </li>
          {d.status !== 'DRAFT' && (
            <li className={styles.timelineItem}>
              <span className={styles.timelineDot} aria-hidden />
              <div className={styles.timelineBody}>
                <span className={styles.timelineTime}>{fmtDateTime(d.updated_at_utc)}</span>
                <span className={styles.timelineTitle}>Status transitioned to {d.status}</span>
                <span className={styles.timelineSub}>
                  {effectivityRows.filter((r) => r.status === 'EFFECTIVE').length} site(s) effective
                </span>
              </div>
            </li>
          )}
          {d.legal_hold_active && (
            <li className={`${styles.timelineItem} ${styles.timeline_hold}`}>
              <span className={styles.timelineDot} aria-hidden />
              <div className={styles.timelineBody}>
                <span className={styles.timelineTitle}>Legal hold active</span>
                <span className={styles.timelineSub}>
                  Retention end {fmtDate(d.retention_end_at)} suspended pending hold release.
                </span>
              </div>
            </li>
          )}
        </ol>
        <p className={styles.advisoryNote}>
          Signature events are recorded in <code>signature.signatures</code> + <code>audit.system_audit_log</code>.
          Full per-version signature stream lands in v0.13 once `document.document_versions` joins are wired.
        </p>
      </section>
    </main>
  );
}
